import React from 'react';
import { t } from 'i18next';
import _ from 'lodash';
import { action, makeObservable, observable } from 'mobx';
import environment from '@environment';
import { CheckListType } from 'components/inputs/checkbox/checkbox-list/checkbox-list';
import { SelectOption } from 'components/inputs/select/select';
import { Tab } from 'components/tabs/simple-tab/simple-tab';
import { DataRequestSteps, ProfileSteps, UpdateProfileSteps } from 'core/enums/profile';
import { RewardLevel } from 'core/enums/rewards-levels';
import { ProfileAddressDTO, ProfileLocation, ProfilePhoneNumber, ProfilePreferences, ProfileUser } from 'core/models/account/profile';
import { FreshDeskHelper } from 'core/services/api/fresh-desk-helper';
import { ProfileService } from 'core/services/api/profile';
import { EndpointHelper } from 'core/services/endpoint.helper';
import { HttpClient } from 'core/services/http';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { ValidationUtil } from 'utils/validation.utils';

const dataRequestOptionsList = [
  {
    text: 'Right to Access',
    value: 'Right to access',
    groupList: 'CEOid',
  },
  {
    text: 'Right to be Forgotten',
    value: 'Right to be forgotten',
    groupList: 'CEOid',
  },
  {
    text: 'Right to Rectification',
    value: 'Right to rectification',
    groupList: 'POPIAid',
  },
  {
    text: 'Right to Object to Processing',
    value: 'Right to object to processing',
    groupList: 'POPIAid',
  },
  {
    text: 'Right to Restriction of Processing',
    value: 'Right to restriction of processing',
    groupList: 'CEOid',
  },
  {
    text: 'Right to Data Portability',
    value: 'Right to data portability',
    groupList: 'CEOid',
  },
  {
    text: 'Right to raise a Complaint',
    value: 'Right to raise a complaint',
    groupList: 'POPIAid',
  },
  {
    text: 'Right to be Informed',
    value: 'Right to be informed',
    groupList: 'POPIAid',
  },
];

const channelIconMap = new Map([
  ['SMS', 'icon-imi-all-messages'],
  ['Email', 'icon-email'],
  ['PhoneCall', 'icon-ico-call-2'],
  ['WhatsApp', 'icon-whatsapp'],
  ['DSD', ''],
]);

const InitialAddress = {
  streetName: '',
  buildingNumber: '',
  buildingName: '',
  suburb: '',
  city: '',
  province: '',
  postalCode: '',
};

export class ProfileStore {
  isLoading = true;
  isLoadingImage = true;
  isLoadingPreference = true;
  isSaving = false;
  isSavingImage = false;
  isUpdating = false;
  isSuccess = false;

  hasPostalAddress = true;

  hasOptionalEmail!: boolean;

  platform!: string;
  country!: string;
  countryCode!: string;
  customerNumber!: string;
  updateStep = UpdateProfileSteps.PersonalDetails;
  profileStep = ProfileSteps.Details;

  user!: ProfileUser;
  channels!: CheckListType[];
  segments!: CheckListType[];
  userImage!: string;
  locations!: ProfileLocation;

  physicalAddress!: ProfileAddressDTO;
  postalAddress!: ProfileAddressDTO;

  phoneNumberUtilService = PhoneNumberUtil.getInstance();
  userMobileNumber!: ProfilePhoneNumber;
  personalMobile = '';
  personalEmail? = '';

  dataRequestOption!: string;
  dataRequestCountryOption!: string;
  profileCountries!: SelectOption[];
  selectedCountry!: string;

  personalFormErrorMessages = {
    displayMobileError: false,
    displayEmailError: false,
  };

  dataRequestOptions = dataRequestOptionsList;
  isLoadingDataRequest = false;
  dataRequestActiveStep = DataRequestSteps.Initial;

  tabs: Tab[] = [
    {
      title: t('profile.tabs.personalDetails'),
      isActive: true,
      component: React.lazy(() => import('./tabs/profile.details')),
    },
    {
      title: t('profile.tabs.myAddresses'),
      isActive: false,
      component: React.lazy(() => import('./tabs/profile.addresses')),
    },
  ];

  get basePhysicalAddress() {
    return this.user?.listOfAddress?.find((o) => o.addressType?.toLowerCase() === 'physical');
  }

  get basePostalAddress() {
    return this.user?.listOfAddress?.find((o) => o.addressType?.toLowerCase() === 'postal');
  }

  get personalDetailsIsValid() {
    return (
      !this.personalFormErrorMessages.displayEmailError &&
      !this.personalFormErrorMessages.displayMobileError &&
      this.personalMobile !== '' &&
      this.personalEmail !== ''
    );
  }

  get dataRequestButtonDisabled() {
    if (this.selectedCountry && this.selectedCountry !== 'za') {
      return false;
    }

    if (this.dataRequestOption) {
      return false;
    }

    return true;
  }

  constructor() {
    makeObservable(this, {
      country: observable,
      countryCode: observable,
      customerNumber: observable,

      isLoading: observable,
      isLoadingImage: observable,
      isLoadingPreference: observable,
      isSaving: observable,
      isSavingImage: observable,
      isUpdating: observable,
      isSuccess: observable,
      hasPostalAddress: observable,
      user: observable,
      updateStep: observable,
      profileStep: observable,
      channels: observable,
      segments: observable,
      dataRequestOption: observable,
      dataRequestCountryOption: observable,
      selectedCountry: observable,
      profileCountries: observable,
      dataRequestOptions: observable,
      isLoadingDataRequest: observable,
      dataRequestActiveStep: observable,

      personalFormErrorMessages: observable,
      userMobileNumber: observable,
      postalAddress: observable,
      physicalAddress: observable,
      personalMobile: observable,
      personalEmail: observable,
      hasOptionalEmail: observable,

      getPreferences: action,

      changeIsLoading: action,
      changeIsSaving: action,
      changeIsSavingImage: action,
      changeIsSuccess: action,
      changeHasPostalAddress: action,
      changeUser: action,
      cancelUpdate: action,
      changeDataRequestOption: action,
      changeDataRequestCountryOption: action,
      changeProfileCountries: action,
      changeSelectedCountry: action,
      setBaseValues: action,
      validateAndFormatPhoneNumber: action,
      changePhysicalAddress: action,
      changePostalAddress: action,
      changeMobileNumber: action,
      changeEmailAddress: action,

      changeIsLoadingImage: action,
      changeUserImage: action,
      changeUserPreferences: action,
      changeIsLoadingPreference: action,
      changeChannels: action,
      changeSegments: action,

      dataRequestSubmit: action,
      changeActiveStepDataRequest: action,
      changeIsLoadingDataRequest: action,
      createFreshDeskTicket: action,
      createFreshDeskTicketPayload: action,
    });
  }

  initialize(
    customerNumber: string,
    country: string,
    countryCode: string,
    platform: string,
    hasPostalAddress = true,
    hasProfileImage = false,
    hasOptionalEmail = false
  ) {
    this.platform = platform;
    this.country = country;
    this.countryCode = countryCode;
    this.customerNumber = customerNumber;
    this.hasOptionalEmail = hasOptionalEmail;

    this.changeIsLoading(true);
    ProfileService.getProfile(customerNumber, country)
      .then((profileUser) => {
        this.changeIsLoading(false);
        this.changeUser(profileUser);
        this.changeHasPostalAddress(hasPostalAddress);
        this.setBaseValues();
      })
      .catch((error) => {
        this.changeIsLoading(false);
        console.error('Failed to retrieve user profile.', error);
      });

    if (hasProfileImage) {
      ProfileService.getProfileImage(customerNumber, country)
        .then((profileImage: any) => {
          this.changeIsLoadingImage(false);
          if (profileImage !== 'Image not available') {
            this.changeUserImage(profileImage);
          }
        })
        .catch((error) => {
          this.changeIsLoadingImage(false);
          console.error('Failed to retrieve user image.', error);
        });
    }
  }

  getPreferences(customerNumber: string, country: string) {
    this.changeIsLoadingPreference(true);
    ProfileService.getPreferences(customerNumber, country)
      .then((prefrences: ProfilePreferences) => {
        this.changeIsLoadingPreference(false);
        this.changeUserPreferences(prefrences);
      })
      .catch((error: any) => {
        this.changeIsLoadingPreference(false);
        console.error('Failed to retrieve user preferences.', error);
      });
  }

  changeIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  changeIsLoadingImage(isLoading: boolean) {
    this.isLoadingImage = isLoading;
  }

  changeIsLoadingPreference(isLoading: boolean) {
    this.isLoadingPreference = isLoading;
  }

  changeIsSaving(isSaving: boolean) {
    this.isSaving = isSaving;
  }

  changeUser(user: ProfileUser) {
    this.user = user;

    // FIXME: Resolve when integrating Rewards & Preferences
    this.user.rewardsLevel = RewardLevel.Super;
  }

  changeUserPreferences(preferences: ProfilePreferences) {
    this.segments = preferences.segments.map((value: any, index: number) => ({
      id: `${index}`,
      name: value.key,
      isChecked: value.value,
    }));
    this.channels = preferences.channels.map((value: any, index: number) => ({
      id: `${index}`,
      name: value.key,
      isChecked: value.value,
      descIcon: channelIconMap.get(value.key),
    }));
  }

  changeSegments(segments: CheckListType[]) {
    this.segments = segments;
  }

  changeChannels(channels: CheckListType[]) {
    this.channels = channels;
  }

  changeUserImage(userImage: any) {
    this.userImage = userImage;
  }

  setBaseValues() {
    const mobileObj = this.user?.listOfPhoneNumber?.find((number) => {
      return number.isPrimary || number.phoneType?.toLowerCase() === 'mobile';
    });
    if (mobileObj) {
      this.userMobileNumber = mobileObj;
    }
    this.changeMobileNumber(mobileObj?.number || ``);
    this.changeEmailAddress(this.user?.email);

    this.physicalAddress = { ...InitialAddress, ...(this.basePhysicalAddress || {}) };
    this.postalAddress = {
      ...InitialAddress,
      ...((this.hasPostalAddress ? this.basePostalAddress : this.basePhysicalAddress) || {}),
    };
  }

  changePhysicalAddress(address: ProfileAddressDTO) {
    this.physicalAddress = address;
  }

  changePostalAddress(address: ProfileAddressDTO) {
    this.postalAddress = address;
  }

  changeUpdateStep(step: UpdateProfileSteps) {
    this.updateStep = step;
  }

  changeIsSavingImage(isSavingImage: boolean) {
    this.isSavingImage = isSavingImage;
  }

  changeIsSuccess(isSuccess: boolean) {
    this.isSuccess = isSuccess;
  }

  changeHasPostalAddress(hasPostalAddress: boolean) {
    this.hasPostalAddress = hasPostalAddress;
  }

  changeDataRequestOption(dataRequestValue: string) {
    this.dataRequestOption = dataRequestValue;
  }

  changeDataRequestCountryOption(dataRequestCountryValue: string) {
    this.dataRequestCountryOption = dataRequestCountryValue;
  }

  changeProfileCountries(profileCountry: SelectOption[]) {
    this.profileCountries = profileCountry;
  }

  changeSelectedCountry(selectedCountry: string) {
    this.selectedCountry = selectedCountry;
  }

  changeMobileNumber(value: string) {
    const { formattedValue, isValid } = this.validateAndFormatPhoneNumber(value);
    this.personalMobile = formattedValue;
    this.personalFormErrorMessages.displayMobileError = !isValid;
  }

  changeEmailAddress(value: string) {
    if (this.hasOptionalEmail && !value) {
      this.personalEmail = undefined;
      this.personalFormErrorMessages.displayEmailError = false;
      return;
    }
    const { isValid } = this.validateEmailAddress(value);
    this.personalEmail = value;
    this.personalFormErrorMessages.displayEmailError = !isValid;
  }

  cancelUpdate() {
    this.setBaseValues();
    this.changeUpdateStep(UpdateProfileSteps.PersonalDetails);
    this.changeProfileStep(ProfileSteps.Details)

  }

  saveProfile(code?:any, contactMethod?: any) {
    const number = this.phoneNumberUtilService.parseAndKeepRawInput(this.personalMobile, this.countryCode);
    const isEmailContact = !!contactMethod && contactMethod.indexOf('@') > -1;
    const isPhoneContact = !!contactMethod && /^[+0-9]/.test(contactMethod);

    const requestObj = {
      firstName: this.user.firstName,
      surname: this.user.surname,
      email: this.personalEmail,
      verifyOtp: {
        otp: code ? code : '',
        ...(isEmailContact
          ? this.user?.email
            ? { emailId: this.user.email }
            : {}
          : isPhoneContact
          ? this.userMobileNumber
            ? {
                phoneNumber: {
                  code: `${this.userMobileNumber.countryCode || ''}`,
                  number: `${this.userMobileNumber.number || ''}`,
                },
              }
            : {}
          : {}),
      },
      listOfPhoneNumber: [
        {
          phoneType: 'Mobile',
          isPrimary: true,
          countryCode: `${number.getCountryCode()}`,
          number: `${number.getNationalNumber()}`,
        },
        ...(this.user?.listOfPhoneNumber?.filter((number) => !number.isPrimary) || []),
      ],
      listOfAddress: [
        {
          addressType: 'Physical',
          houseNumber: null,
          floor: null,
          streetName: this.physicalAddress.streetName,
          buildingNumber: this.physicalAddress.buildingNumber,
          buildingName: this.physicalAddress.buildingName,
          suburb: this.physicalAddress.suburb,
          city: this.physicalAddress.city,
          province: this.physicalAddress.province,
          postalCode: this.physicalAddress.postalCode,
          postBoxNumber: null,
        },
        ...(this.hasPostalAddress
          ? [
              {
                addressType: 'Postal',
                houseNumber: null,
                floor: null,
                streetName: this.postalAddress.streetName,
                buildingNumber: this.postalAddress.buildingNumber,
                buildingName: this.postalAddress.buildingName,
                suburb: this.postalAddress.suburb,
                city: this.postalAddress.city,
                province: this.postalAddress.province,
                postalCode: this.postalAddress.postalCode,
                postBoxNumber: null,
              },
            ]
          : []),
      ],
    };

    this.changeIsSaving(true);
    ProfileService.updateProfile(this.customerNumber, this.country, requestObj)
      .then((_) => {
        this.changeIsSuccess(true);
        this.changeUpdateStep(UpdateProfileSteps.Notice);
        this.changeIsSaving(false);
      })
      .catch((error) => {
        this.changeIsSuccess(false);
        this.changeUpdateStep(UpdateProfileSteps.Notice);
        this.changeIsSaving(false);
        console.error('Failed to retrieve user profile.', error);
      });
  }

  saveProfileImage(image: string) {
    this.changeIsSavingImage(true);

    ProfileService.updateProfileImage(this.customerNumber, this.country, image).then(
      (_) => {
        this.changeUserImage(image);
        this.changeIsSavingImage(false);
      },
      (error) => {
        this.changeUserImage(image);
        this.changeIsSavingImage(false);
        console.error('Failed to retrieve user profile.', error);
      }
    );
  }

  updateProfilePreferences() {
    this.changeIsLoadingPreference(true);

    return ProfileService.updatePreferences(this.customerNumber, this.country, {
      segments: this.segments.map((value: any) => ({
        key: value.name,
        value: value.isChecked,
      })),
      channels: this.channels.map((value: any) => ({
        key: value.name,
        value: value.isChecked,
      })),
    }).then(
      (_) => {
        this.changeIsSuccess(true);
        this.changeIsLoadingPreference(false);
      },
      (error) => {
        this.changeIsSuccess(false);
        console.error('Failed to update user preferences.', error);
        this.changeIsLoadingPreference(false);
      }
    );
  }

  getProfileCountry() {
    this.changeIsLoading(true);
    return ProfileService.getCountriesFromMenu()
      .then((res: SelectOption[]) => this.changeProfileCountries(res))
      .finally(() => this.changeIsLoading(false));
  }

  validateEmailAddress(value: string) {
    return {
      value: value,
      isValid: value && ValidationUtil.ValidateEmail(value),
    };
  }

  validateAndFormatPhoneNumber(value: string) {
    const code = this.countryCode.toUpperCase();
    const possible = this.phoneNumberUtilService.isPossibleNumberString(`${value}`, code);
    const number = possible && this.phoneNumberUtilService.parseAndKeepRawInput(`${value}`, code);
    const formatted = number && this.phoneNumberUtilService.format(number, PhoneNumberFormat.INTERNATIONAL);

    return {
      formattedValue: possible && number && formatted ? formatted : value,
      isValid: number && this.phoneNumberUtilService.isValidNumber(number),
    };
  }

  // FreshDesk Attempt clean up
  createFreshDeskTicketPayload() {
    const ticketSourceCode = 2;
    const ticketStatusCode = 2;
    const ticketStatusPriorityCode = 2;

    const groupIdSet = this.getFreshDeskGroupSet(this.dataRequestOption);

    const subjectValues = {
      dataRequestOption: this.dataRequestOption,
    };

    const descriptionValues = {
      dataRequestOption: this.dataRequestOption,
      platform: this.platform
    };

    const ticketPayload: any = {
      source: ticketSourceCode,
      status: ticketStatusCode,
      subject: t(`freshDesk.dataRequestTicket.subject`, subjectValues),
      description: t(`freshDesk.dataRequestTicket.description`, descriptionValues),
      priority: ticketStatusPriorityCode,
      email: this.personalEmail,
      custom_fields: {
        cf_customer_number: this.customerNumber,
        cf_dg_country: this.profileCountries.find((country) => country.value === this.selectedCountry)?.text,
      },
    };

    if (groupIdSet === 'CEOid') {
      ticketPayload.group_id = +environment.FRESHDESK_Data_REQUEST_GROUP_CEO_ID;
      ticketPayload.custom_fields = {
        ...ticketPayload.custom_fields,
        [environment.FRESHDESK_Data_REQUEST_CEO_ID_CF]: this.dataRequestOption,
      };
    }

    if (groupIdSet === 'POPIAid') {
      ticketPayload.group_id = +environment.FRESHDESK_Data_REQUEST_GROUP_POPIA_ID;
      ticketPayload.custom_fields = {
        ...ticketPayload.custom_fields,
        [environment.FRESHDESK_Data_REQUEST_POPIA_ID_CF]: this.dataRequestOption,
      };
    }

    return ticketPayload;
  }

  async createFreshDeskTicket() {
    // fail safe checks
    if (!(this.customerNumber && this.personalEmail)) {
      this.changeActiveStepDataRequest(DataRequestSteps.Fail);
      this.changeIsLoadingDataRequest(false);
      return;
    }

    // create payload
    const ticketPayload = this.createFreshDeskTicketPayload();

    // create ticket callback - boolean response
    const ticketCreated = await FreshDeskHelper.submitFreshDeskTicket(
      this.customerNumber,
      this.personalEmail,
      ticketPayload
    );

    this.changeActiveStepDataRequest(ticketCreated ? DataRequestSteps.Success : DataRequestSteps.Fail);

    this.changeIsLoadingDataRequest(false);
  }

  // FreshDesk Request Data
  getFreshDeskGroupSet(right: string) {
    const item = this.dataRequestOptions.find((acc) => acc.value === right);
    return item?.groupList || '';
  }

  changeIsLoadingDataRequest(isLoading: boolean) {
    this.isLoadingDataRequest = isLoading;
  }

  dataRequestSubmit() {
    this.changeIsLoadingDataRequest(true);
    this.createFreshDeskTicket();
  }

  changeActiveStepDataRequest(step: DataRequestSteps) {
    this.dataRequestActiveStep = step;
  }

  changeProfileStep(step: ProfileSteps) {
    this.profileStep = step;
  }
}